'use client';

import Image from 'next/image';
import Link from 'next/link';
import { Facebook, Twitter, Instagram, Linkedin, Mail, MapPin } from 'lucide-react';

export default function Footer() {
    const currentYear = new Date().getFullYear();

    return (
        <footer className="bg-surface-inset border-t border-border-subtle pt-16 pb-8">
            <div className="container px-6 mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 mb-16">

                    {/* Brand Column (4 cols) */}
                    <div className="lg:col-span-4">
                        <div className="flex items-center gap-4 mb-6">
                            <Link href="/" className="inline-block hover:opacity-80 transition-opacity">
                                <img
                                    src="/logo/carsite.png"
                                    alt="CarSite Logo"
                                    className="h-12 w-auto object-contain light-mode-only"
                                />
                                <img
                                    src="/logo/carsite-white.png"
                                    alt="CarSite Logo (Dark)"
                                    className="h-12 w-auto object-contain dark-mode-only"
                                />
                            </Link>
                            <img
                                src="/carsite-alpha-logo.png"
                                alt="Alpha Version"
                                className="object-contain light-mode-only h-8 w-auto"
                            />
                            <img
                                src="/carsite-alpha-logo-white.png"
                                alt="Alpha Version (Dark)"
                                className="object-contain dark-mode-only h-8 w-auto"
                            />
                        </div>
                        <p className="text-text-secondary leading-relaxed mb-6 max-w-sm">
                            South Africa's premium automotive information platform.
                            Delivering expert reviews, latest news, and accurate specifications for the modern car buyer.
                        </p>
                        <div className="flex gap-4">
                            <SocialIcon icon={<Facebook size={18} />} href="#" label="Facebook" />
                            <SocialIcon icon={<Twitter size={18} />} href="#" label="Twitter" />
                            <SocialIcon icon={<Instagram size={18} />} href="#" label="Instagram" />
                            <SocialIcon icon={<Linkedin size={18} />} href="#" label="LinkedIn" />
                        </div>
                    </div>

                    {/* Links Column 1: Explore (2 cols) */}
                    <div className="lg:col-span-2 lg:col-start-6">
                        <h4 className="font-bold text-text-primary mb-6 uppercase text-sm tracking-wider">Explore</h4>
                        <ul className="space-y-4">
                            <FooterLink href="/articles" label="Latest News" />
                            {/* <FooterLink href="/reviews" label="Car Reviews" /> */}
                            {/* <FooterLink href="/buying-guides" label="Buying Guides" />
                            <FooterLink href="/videos" label="Videos" /> */}
                            <FooterLink href="/advertise" label="Advertise" />
                            <FooterLink href="/contact" label="Contact" />
                        </ul>
                    </div>

                    {/* Links Column 2: Company (2 cols) */}
                    <div className="lg:col-span-2">
                        <h4 className="font-bold text-text-primary mb-6 uppercase text-sm tracking-wider">Company</h4>
                        <ul className="space-y-4">
                            <FooterLink href="/about-us" label="About Us" />
                            <FooterLink href="/mission" label="Mission & Vision" />
                            <FooterLink href="/partners" label="Our Partners" />
                            <FooterLink href="/careers" label="Careers" />
                        </ul>
                    </div>

                    {/* Links Column 3: Legal (2 cols) */}
                    <div className="lg:col-span-2">
                        <h4 className="font-bold text-text-primary mb-6 uppercase text-sm tracking-wider">Legal</h4>
                        <ul className="space-y-4">
                            <FooterLink href="/privacy-policy" label="Privacy Policy" />
                            <FooterLink href="/terms" label="Terms of Service" />
                            <FooterLink href="/cookie-policy" label="Cookie Policy" />
                            <FooterLink href="/disclaimer" label="Disclaimer" />
                        </ul>
                    </div>
                </div>

                {/* Bottom Bar */}
                <div className="pt-8 border-t border-border-subtle flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-text-secondary">
                    <div>
                        &copy; {currentYear} CarSite.co.za. All rights reserved.
                    </div>
                    <div className="flex gap-6">
                        <span className="flex items-center gap-2">
                            <MapPin size={14} /> Johannesburg, South Africa
                        </span>
                        <a href="mailto:hello@carsite.co.za" className="flex items-center gap-2 hover:text-brand transition-colors">
                            <Mail size={14} /> hello@carsite.co.za
                        </a>
                    </div>
                </div>
            </div>
        </footer>
    );
}

// Helper Components for cleaner code
function FooterLink({ href, label }: { href: string; label: string }) {
    return (
        <li>
            <Link href={href} className="text-text-secondary hover:text-brand transition-colors text-sm">
                {label}
            </Link>
        </li>
    );
}

function SocialIcon({ icon, href, label }: { icon: React.ReactNode; href: string; label: string }) {
    return (
        <a href={href} aria-label={label} className="w-10 h-10 rounded-full bg-surface-elevated border border-border-subtle flex items-center justify-center text-text-secondary hover:bg-brand hover:text-text-inverse hover:border-brand transition-all duration-300">
            {icon}
        </a>
    );
}
